import React, {memo, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useProfile} from "../../../contexts/ProfileContext.tsx";
import MotionSemAutorizacao from "../../../core/components/MotionSemAutorizacao.tsx";
import MotionContainer from "../../../core/components/MotionContainer.tsx";
import Layout from "../../../core/layout/Layout.tsx";
import useService from "../../../hooks/useService.ts";
import {
    buscarDocumentoContratoPrestacaoServico,
    buscarTodosContratosPrestacaoServico,
    deletarContratoPrestacaoServico
} from "../../../services/ModuloGerenciador/PrestacaoDeServico.ts";
import Separator from "../../../core/components/Separator.tsx";
import TabelaV2 from "../../../components/TabelaV2/TabelaV2";
import CustomButtonV2 from "../../../components/CustomButtonV2/CustomButtonV2";
import {useNavigate} from "react-router-dom";
import {primeiroDiaDosMesFormatado, ultimoDiaDosMesFormatado} from "../../../core/utils/UtilityDates.ts";
import {PrestacaoServico} from "../../../models/PrestacaoServico.ts";
import FiltroData from "../../../core/data-entry/FiltroData.tsx";
import {useEmbarqueDependencias} from "../../../contexts/EmbarqueDependenciasContext.tsx";
import SelectInputField from "../../../core/data-entry/SelectInputField.tsx";
import IconButton from "../../../core/components/IconButton.tsx";
import {useTabela} from "../../../contexts/Tabela.js";
import {useToast} from "../../../contexts/ToastContext.tsx";
import ModalConfirmacao from "../../../components/ModalConfirmacao/ModalConfirmacao.tsx";
import {base64ToBlob} from "../../../core/utils/base64ToBlob.ts";

const filtroInicial = {
    dataInicial: primeiroDiaDosMesFormatado,
    dataFinal: ultimoDiaDosMesFormatado,
    empresaSubId: -1,
};

function PrestacaoDeServico() {
    const navigate = useNavigate();

    const {profile, hasRegra, tabelaEmpresa, isLoading: isLoadingContext} = useProfile();
    const {empresaSub, fetchEmpresaSub} = useEmbarqueDependencias();
    const {tabelaRedux, setTabelaRedux} = useTabela();
    const {showToast} = useToast();

    const buscouContratosInicial = useRef(false);

    const defaultEmpresaSubOptions = useMemo(() => {
        return empresaSub.map((empresa) => ({
            title: empresa.nome,
            obj: empresa.idEmpresaSub,
        }));
    }, [empresaSub]);

    const [semAutorizacao, setSemAutorizacao] = useState(false);
    const [contratos, setContratos] = useState<PrestacaoServico[]>([]);
    const [filtro, setFiltro] = useState<{
        dataInicial: string;
        dataFinal: string;
        empresaSubId: number;
    }>(filtroInicial);
    const [valueSelect, setValueSelect] = useState<{ title: string; obj: string }>({title: "", obj: ""});
    const [empresaOptions, setEmpresaOptions] = useState<{ title: string; obj: string }[]>(defaultEmpresaSubOptions);
    const [openModalConfirmacao, setOpenModalConfirmacao] = useState(false);

    const [buscandoContratos, buscarContratos] = useService(buscarTodosContratosPrestacaoServico, {
        autoStart: false,
        onData: (response) => {
            setContratos(response);
        },
    });

    const [, baixarContratoDocumento] = useService(buscarDocumentoContratoPrestacaoServico,
        {
            autoStart: false,
            onData: async (response) => {
                const text = await response.text();
                const {fileBase64, fileName} = JSON.parse(text);

                const blob = await base64ToBlob(fileBase64);
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", fileName);
                document.body.appendChild(link);
                link.click();
            },
            onError: (error) => {
                showToast(error.response?.data || "Erro ao baixar documento do contrato de prestação de serviço", "error");
            }
        });

    const [, deletarContrato] = useService(deletarContratoPrestacaoServico,
        {
            autoStart: false,
            onData: () => {
                buscarContratosFiltro();
                showToast("Contrato de prestação de serviço deletado com sucesso", "success");
            },
            onError: (error) => {
                showToast(error.response?.data || "Erro ao deletar contrato de prestação de serviço", "error");
            }
        });

    const buscarContratosInicial = useCallback(() => {
        if (profile?.token) {
            buscarContratos({
                token: profile.token,
                dataInicial: primeiroDiaDosMesFormatado,
                dataFinal: ultimoDiaDosMesFormatado,
                empresaSubId: -1,
            });
        }
        ;
    }, [profile?.token, buscarContratos]);

    const handleInputEmpresaChange = useCallback((inputValue) => {
        const normalizedInput = inputValue.target.value.trim().toLowerCase();

        if (normalizedInput === "") {
            setEmpresaOptions(defaultEmpresaSubOptions);
            return;
        }

        setEmpresaOptions(empresaSub
            .filter((empresa) => {
                return empresa.nome.toLowerCase().includes(normalizedInput);
            })
            .sort((a, b) => a.nome.localeCompare(b.nome))
            .map((empresa) => ({
                title: empresa.nome,
                obj: empresa.idEmpresaSub,
            })));
    }, [empresaSub, defaultEmpresaSubOptions]);

    const handleEmpresaChange = useCallback((selectedOption: { title: string; obj: string } | null) => {
        if (selectedOption) {
            setValueSelect(selectedOption);
            setFiltro((prevFiltro) => ({
                ...prevFiltro,
                empresaSubId: Number(selectedOption.obj),
            }));
        }
    }, []);

    const buscarContratosFiltro = useCallback(() => {
        if (profile?.token) {
            buscarContratos({
                token: profile.token,
                dataInicial: filtro.dataInicial,
                dataFinal: filtro.dataFinal,
                empresaSubId: filtro.empresaSubId,
            });
        }
    }, [profile?.token, buscarContratos, filtro]);

    const clearSearch = useCallback(() => {
        setFiltro(filtroInicial);
        setValueSelect({title: "", obj: ""});
        buscarContratosInicial();
    }, [buscarContratosInicial]);

    const handleOpenDeleteContrato = useCallback(() => {
        if (tabelaRedux.colunaSelecionada) {
            setOpenModalConfirmacao(true);
        }
    }, [tabelaRedux.colunaSelecionada]);

    const handleDeletarContrato = useCallback(() => {
        deletarContrato({
            token: profile?.token || "",
            idPrestacaoServico: tabelaRedux.colunaSelecionada || "",
        })
        setTabelaRedux({});
    }, [deletarContrato, profile?.token, tabelaRedux.colunaSelecionada, setTabelaRedux]);

    const handleClearEmpresa = useCallback(() => {
        setValueSelect(defaultEmpresaSubOptions);
        setFiltro((prevFiltro) => ({
            ...prevFiltro,
            empresaSubId: -1,
        }));
    }, [defaultEmpresaSubOptions]);

    useEffect(() => {
        if (profile?.token) {
            setSemAutorizacao(!hasRegra("Prestação de Serviço"));
            if (!buscouContratosInicial.current) {
                buscarContratosInicial();
                buscouContratosInicial.current = true;
                setTabelaRedux({});
            }
        }
    }, [profile?.token, hasRegra, buscarContratosInicial, setTabelaRedux]);

    useEffect(() => {
        if (!isLoadingContext && !empresaSub.length && profile?.token) {
            fetchEmpresaSub({token: profile.token});
        }
    }, [isLoadingContext, empresaSub.length, profile?.token, fetchEmpresaSub]);

    const dadosTabela = useMemo(() => {
        return contratos.map((contrato) => {
            const rota = contrato.embarque.pedidoEmbarque?.rota;
            const rotaCidadeDestino = rota?.cidadeDestinoRota.abreviacao ?? "";
            const rotaEstadoDestino = rota?.cidadeDestinoRota.estado ?? "";
            const rotaCidadeOrigem = rota?.cidadeOrigemRota.abreviacao ?? "";
            const rotaEstadoOrigem = rota?.cidadeOrigemRota.estado ?? "";
            const rotaString = rotaCidadeOrigem && rotaCidadeDestino ? `${rotaCidadeOrigem} - ${rotaEstadoOrigem} - ${rotaCidadeDestino} - ${rotaEstadoDestino}` : "";
            return (
                {
                    empresa: contrato.empresaSub.nomeSocial
                        ??
                        "",
                    rota:
                    rotaString,
                    pedido:
                        contrato.embarque.pedidoEmbarque?.pedido ?? "",
                    cte:
                    contrato.embarque.cte,
                    veiculoMotorista:
                        contrato.embarque.motoristaSelecionado ?? {nome: ""},
                    placaCavalo:
                        contrato.embarque.veiculoEmbarque?.caminhao?.placa ?? "",
                    usuario:
                    contrato.usuario,
                    data:
                    contrato.data,
                    idPrestacaoDeServico:
                    contrato.id,
                }
            )
        });
    }, [contratos]);

    const isLoading = useMemo(() => {
        return !profile?.token || buscandoContratos;
    }, [profile?.token, buscandoContratos]);

    if (semAutorizacao) {
        return <MotionSemAutorizacao/>;
    }

    return (
        <Layout title="Prestação de Serviços">
            <MotionContainer>
                <div className="content-container">
                    <Separator size="xl"/>
                    <div className="filtro-prestacao-cadastro">
                        <div className="filtro-data-container">
                            <FiltroData
                                title="Início"
                                value={filtro.dataInicial}
                                onChange={(value) =>
                                    setFiltro({
                                        ...filtro,
                                        dataInicial: value,
                                    })
                                }
                            />
                            <div className="filtro-data-divider"/>
                            <FiltroData
                                title="Fim"
                                titleFrente={false}
                                value={filtro.dataFinal}
                                onChange={(value) =>
                                    setFiltro({
                                        ...filtro,
                                        dataFinal: value,
                                    })
                                }
                            />
                        </div>
                        <Separator size="md" direction="horizontal"/>
                        <SelectInputField
                            placeholder="Selecione a empresa subsidiária"
                            value={valueSelect.title || ""}
                            options={empresaOptions}
                            onClear={handleClearEmpresa}
                            onChange={handleEmpresaChange}
                            onInputChange={handleInputEmpresaChange}
                        />
                        <Separator size="md" direction="horizontal"/>
                        <IconButton
                            icon="Search"
                            size="xl"
                            theme="dark"
                            backgroundColor="#FFFFFF"
                            hoverColor="#CCC"
                            onClick={buscarContratosFiltro}
                            disabled={buscandoContratos}
                        />
                        <Separator size="sm" direction="horizontal"/>
                        <IconButton
                            disabled={buscandoContratos}
                            icon="X"
                            size="xl"
                            borderColor="none"
                            onClick={clearSearch}
                            backgroundColor="rgba(0, 0, 0, 0)"
                            theme="dark"
                        />
                    </div>
                    <Separator size="xl"/>
                    <TabelaV2
                        classeSelecionado="idPrestacaoDeServico"
                        nomeClasse1="outer-wrapper-v2"
                        nomeClasse2="table-v2"
                        colunas={tabelaEmpresa?.PrestacaoDeServico}
                        dados={dadosTabela}
                        isLoading={isLoading}
                    />
                    <Separator size="xl"/>
                </div>
                <div className="footer-space-between">
                    <div className="footer-left-buttons-prestacao">
                        <CustomButtonV2
                            classCss="custom-button-cadastrar"
                            classText="button-texto-cadastrar"
                            icone="iconeCheck"
                            titulo="Cadastrar"
                            onClick={() => {
                                navigate("cadastro")
                            }}
                        />
                        <CustomButtonV2
                            classCss="custom-button-cadastrar"
                            classText="button-texto-cadastrar"
                            icone="iconeCheck"
                            titulo="Baixar Contrato"
                            onClick={() => {
                                if (tabelaRedux.colunaSelecionada) {
                                    baixarContratoDocumento({
                                        token: profile?.token || "",
                                        idContrato: tabelaRedux.colunaSelecionada,
                                    });
                                } else {
                                    showToast("Selecione um contrato para baixar o documento", "warning");
                                }
                            }}
                        />
                    </div>
                    <CustomButtonV2
                        classCss="custom-button-deletar"
                        classText="button-texto-cadastrar"
                        titulo="Deletar"
                        icone="iconeDelete"
                        cor="#DC2D2D"
                        onClick={handleOpenDeleteContrato}
                    />
                </div>
            </MotionContainer>
            <ModalConfirmacao
                titulo="Deletar"
                texto="Deseja Deletar?"
                open={openModalConfirmacao}
                onClose={() => setOpenModalConfirmacao(false)}
                onClick={handleDeletarContrato}
            />
        </Layout>
    )
}

export default memo(PrestacaoDeServico);